import { motion } from 'framer-motion';
import { useLanguage } from '@/i18n/LanguageContext';
import { Factory, Zap, HeartPulse, Hammer, Wrench, Plane } from 'lucide-react';

/**
 * Applications Section — industry verticals from the pitch deck.
 * Swiss-minimalist, no gradients.
 */
export default function ApplicationsSection() {
  const { language } = useLanguage();
  const isTurkish = language === 'tr';

  const industries = [
    {
      id: 'manufacturing',
      icon: Factory,
      title: isTurkish ? 'Üretim' : 'Manufacturing',
      desc: isTurkish ? 'Robotik hatlar, fikstürler ve özel aparatlar için ön tasarım ve üretim-planlama desteği; uzman doğrulaması gerekir.' : 'Preliminary design and manufacturing-planning support for robotic lines, fixtures and custom tooling; qualified review required.',
      stats: isTurkish ? 'Pilot etkisi: TBD · temel ölçüm gerekli' : 'Pilot impact: TBD · baseline required',
    },
    {
      id: 'energy',
      icon: Zap,
      title: isTurkish ? 'Enerji' : 'Energy',
      desc: isTurkish ? 'Solar/rüzgar/batarya kurulumları için modüler yapı prototipleri.' : 'Modular structure prototypes for solar/wind/battery installations.',
      stats: isTurkish ? 'Pilot etkisi: TBD · maliyet temeli gerekli' : 'Pilot impact: TBD · cost baseline required',
    },
    {
      id: 'medical',
      icon: HeartPulse,
      title: isTurkish ? 'Medikal' : 'Medical',
      desc: isTurkish ? 'Özel cihazlar, cerrahi aletler ve protezler için tasarım.' : 'Design for custom devices, surgical tools, and prosthetics.',
      stats: isTurkish ? 'Konsept iş akışı · klinik kullanım veya FDA onayı değildir' : 'Concept workflow · not clinical use or FDA approval',
    },
    {
      id: 'construction',
      icon: Hammer,
      title: isTurkish ? 'İnşaat' : 'Construction',
      desc: isTurkish ? 'Prefabrik bileşenler, şantiye ekipmanı ve mimari detaylar.' : 'Prefab components, site equipment, and architectural details.',
      stats: isTurkish ? 'BIM ihracatı · yol haritası' : 'BIM export · roadmap',
    },
    {
      id: 'maintenance',
      icon: Wrench,
      title: isTurkish ? 'Bakım' : 'Maintenance',
      desc: isTurkish ? 'AR destekli arıza tespiti ve onarım rehberleri (Beta).' : 'AR-assisted fault detection and repair guidance (Beta).',
      stats: isTurkish ? 'Mobil/tablet beta · saha doğrulaması gerekli' : 'Mobile/tablet beta · field validation required',
    },
    {
      id: 'defense',
      icon: Plane,
      title: isTurkish ? 'Havacılık & Savunma' : 'Aerospace & Defense',
      desc: isTurkish ? 'Kontrollü havacılık ve savunma konsept iş akışları; ihracat kontrolü, güvenlik ve uzman incelemesi zorunludur.' : 'Controlled aerospace and defense concept workflows; export-control, safety and qualified review are mandatory.',
      stats: isTurkish ? 'ITAR/ihracat kontrolü · hukuk incelemesi gerekli' : 'ITAR/export control · legal review required',
    },
  ];

  return (
    <section id="applications" className="relative py-24 md:py-32 bg-[#0A0A0A] overflow-hidden" data-testid="applications-section">
      <div className="pointer-events-none absolute bottom-0 left-0 w-[500px] h-[500px] bg-[#FF3B30] opacity-[0.04] blur-[140px] rounded-full" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.6 }}
          className="mb-16 max-w-2xl"
        >
          <span className="inline-block px-3 py-1.5 border border-white/10 text-xs font-mono uppercase tracking-wider text-white/60 mb-6">
            {isTurkish ? 'Uygulama Alanları' : 'Applications'}
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-light tracking-tight text-white leading-[1.1] mb-4">
            {isTurkish ? (<>Altı sektör, <span className="font-bold">tek platform</span></>) : (<>Six industries, <span className="font-bold">one platform</span></>)}
          </h2>
          <p className="text-white/60 max-w-xl leading-relaxed">
            {isTurkish
              ? 'Fikirden ön mühendislik iş akışlarına — sektöre özel yapılandırma ve uzman incelemesi zorunludur.'
              : 'From idea to preliminary engineering workflows — industry-specific configuration and qualified review remain essential.'}
          </p>
        </motion.div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10">
          {industries.map((ind, i) => {
            const Icon = ind.icon;
            return (
              <motion.div
                key={ind.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-50px' }}
                transition={{ duration: 0.5, delay: i * 0.07 }}
                className="group bg-[#0A0A0A] p-8 hover:bg-[#0D0D0D] transition-colors"
                data-testid={`application-${ind.id}`}
              >
                <div className="w-10 h-10 border border-white/20 flex items-center justify-center mb-6 group-hover:border-white/40 transition-colors">
                  <Icon className="w-4 h-4 text-white/80" />
                </div>
                <h3 className="text-lg font-medium text-white mb-2">{ind.title}</h3>
                <p className="text-sm text-white/60 leading-relaxed mb-5">{ind.desc}</p>
                <div className="pt-4 border-t border-white/10">
                  <span className="text-[10px] font-mono uppercase tracking-widest text-white/50">
                    ↗ {ind.stats}
                  </span>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
